import axiosInstance from '../fetchConfig/axiosConfig';
import { toast } from './toast';
import logger from '../utils/logger';

export const SALE_ISSUE_OPTIONS = [
  {
    value: 'NotArmsLength',
    name: 'NAL sale',
    description: "Non-arm's-length sale",
  },
  {
    value: 'InventoryMismatch',
    name: 'Inventory mismatch',
    description: 'Property was renovated, but the sale reflects the old inventory',
  },
  // {
  //   value: 'SaleConditionIssue',
  //   name: 'Sale condition issue',
  //   description: 'Sold as is, or the condition at sale does not reflect the property',
  // },
];

/** SaleIssue is a list of {SaleIssue, Memo, UserID, TimeStamp}; SQL sources send it as a JSON string. */
export const parseSaleIssues = (saleIssue) => {
  if (Array.isArray(saleIssue)) return saleIssue;
  if (typeof saleIssue !== 'string' || !saleIssue.trim()) return [];
  try {
    const parsed = JSON.parse(saleIssue);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const hasSaleIssue = (saleIssue, issue) =>
  parseSaleIssues(saleIssue).some((entry) => entry?.SaleIssue === issue);

/**
 * Append one SaleIssue on Sales.SalesCombined for this TransactionID.
 * @param {Object} saleData
 * @param {number|string} saleData.TransactionID
 * @param {string} saleData.SaleIssue
 * @param {string} [saleData.Memo]
 * @param {number|string} saleData.UserID
 */
export const flagSingleBadSale = async (saleData) => {
  try {
    const transactionId = saleData.TransactionID;
    if (transactionId == null || transactionId === '') {
      const message = 'No sale transaction ID on this record';
      toast.error(message, { position: 'top-right', autoClose: 2000 });
      return { success: false, message };
    }
    if (!SALE_ISSUE_OPTIONS.some((option) => option.value === saleData.SaleIssue)) {
      const message = 'Choose a sale issue';
      toast.error(message, { position: 'top-right', autoClose: 2000 });
      return { success: false, message };
    }
    if (saleData.UserID == null || saleData.UserID === '') {
      const message = 'Missing user';
      toast.error(message, { position: 'top-right', autoClose: 2000 });
      return { success: false, message };
    }

    const saleObj = {
      TransactionID: transactionId,
      SaleIssue: saleData.SaleIssue,
      Memo: saleData.Memo || '',
      UserID: saleData.UserID,
    };
    
    logger.log('Flagging bad sale:', saleObj);

    const response = await axiosInstance.post('/flag-bad-sale', { saleObj });
    logger.log('Response from flag bad sale:', response);
    
    const respMsg = response?.data?.message || 'Unknown response';
    
    if (respMsg.toLowerCase().includes('no action')) {
      toast.info(respMsg, { position: 'top-right', autoClose: 2000 });
      return { success: false, message: respMsg };
    } else if (respMsg.toLowerCase().includes('successfully')) {
      toast.success(respMsg + ' Re-running algorithm...', { position: 'top-right', autoClose: 2000 });
      return { success: true, message: respMsg };
    } else {
      toast.info(respMsg, { autoClose: 2000 });
      return { success: false, message: respMsg };
    }
  } catch (error) {
    logger.error('Error flagging bad sale:', error);
    throw error;
  }
};


// chat did this, so stupid but this will function for now and will work for bulk flagging.
/**
 * Helper function to flag multiple bad sales
 * @param {Array} salesData - Array of sale objects to flag
 * @param {Function} onProgress - Optional callback for progress updates
 * @returns {Promise<Object>} - Result of the bulk flag bad sale operation
 */
export const flagMultipleBadSales = async (salesData, onProgress = null) => {
  try {
    const results = {
      successful: 0,
      failed: 0,
      total: salesData.length,
      errors: []
    };

    for (let i = 0; i < salesData.length; i++) {
      const sale = salesData[i];
      
      try {
        if (sale.TransactionID == null || sale.TransactionID === '') {
          results.failed++;
          results.errors.push({ error: 'Missing TransactionID' });
          continue;
        }
        const result = await flagSingleBadSale(sale);
        if (result?.success) results.successful++;
        else {
          results.failed++;
          results.errors.push({
            TransactionID: sale.TransactionID,
            error: result?.message || 'Unknown error'
          });
        }
        
        if (onProgress) {
          onProgress(i + 1, salesData.length);
        }
      } catch (error) {
        results.failed++;
        results.errors.push({
          TransactionID: sale.TransactionID,
          error: error.message || 'Unknown error'
        });
      }
    }

    // Show summary toast
    if (results.successful > 0 && results.failed === 0) {
      toast.success(`Successfully flagged ${results.successful} sale${results.successful > 1 ? 's' : ''}`, { position: 'top-right', autoClose: 3000 });
    } else if (results.successful > 0 && results.failed > 0) {
      toast.warning(`Flagged ${results.successful} sale${results.successful > 1 ? 's' : ''}, ${results.failed} failed`, { position: 'top-right', autoClose: 3000 });
    } else if (results.failed > 0) {
      toast.error(`Failed to flag ${results.failed} sale${results.failed > 1 ? 's' : ''}`, { position: 'top-right', autoClose: 3000 });
    }

    return results;
  } catch (error) {
    logger.error('Error in bulk flag bad sales:', error);
    toast.error('Failed to process bulk flag bad sales', { position: 'top-right', autoClose: 2000 });
    throw error;
  }
};

/**
 * Helper function to prepare sales data from selected rows
 * @param {Array} selectedRows - Array of selected row objects
 * @returns {Array} - Array of sale objects ready for flagging
 */
export const prepareSalesData = (selectedRows, flag) => {
  return selectedRows.map(row => ({
    TransactionID: row.TransactionID,
    SaleIssue: flag.SaleIssue,
    Memo: flag.Memo,
    UserID: flag.UserID,
  }));
};
